import { Mic, ArrowRight, Play, ExternalLink, FileDown } from "lucide-react";
import AskAQuestion from "@/components/AskAQuestion";
import RecordingCalendar from "@/components/atfq/RecordingCalendar";
import FeaturedVoices from "@/components/atfq/FeaturedVoices";
import { API } from "@/lib/api";
import {
  YOUTUBE_URL,
  ATFQ_LOGO,
  SUBSCRIBE_LINKS,
  PILLARS,
} from "@/components/atfq/constants";

const PodcastIntro = () => (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
    <aside className="lg:col-span-4">
      <img
        src={ATFQ_LOGO}
        alt="ATFQ · Answer The F**king Question — podcast logo"
        data-testid="atfq-logo"
        className="w-full max-w-xs mx-auto lg:mx-0 aspect-square object-contain"
        loading="lazy"
      />
    </aside>

    <div className="lg:col-span-8">
      <div className="flex items-center gap-2 mb-5 text-xs tracking-tight text-slate-500">
        <Mic className="w-3.5 h-3.5 text-[var(--accent)]" />
        <span>The Podcast</span>
      </div>
      <h2
        id="atfq-heading"
        className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
      >
        ATFQ.
      </h2>
      <div className="mt-2 text-sm md:text-base text-slate-500 tracking-tight">
        Answer The F**king Question
      </div>
      <p className="mt-6 text-lg md:text-xl text-slate-700 leading-relaxed max-w-2xl">
        Declaring independence — from party, from gatekeepers, from easy
        answers. A watchdog podcast built on radical transparency and
        data-driven inquiry, broadcasting from Georgia to the whole
        country.
      </p>

      <div className="mt-8 flex flex-wrap gap-3">
        <a
          href={YOUTUBE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="atfq-cta-listen"
          className="inline-flex items-center gap-3 bg-[var(--accent)] hover:bg-[#3b7bd0] text-white px-6 py-3.5 text-sm font-medium tracking-tight transition-colors"
        >
          <Play className="w-4 h-4 fill-current" />
          Watch on YouTube
          <ArrowRight className="w-4 h-4" />
        </a>
        <a
          href={`${API}/atfq/media-kit.pdf`}
          data-testid="atfq-media-kit-download"
          className="inline-flex items-center gap-3 border border-slate-300 hover:border-[#0A192F] px-6 py-3.5 text-sm font-medium tracking-tight transition-colors"
        >
          <FileDown className="w-4 h-4" />
          Media Kit · PDF
        </a>
      </div>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="atfq-subscribe-row">
        {SUBSCRIBE_LINKS.map(({ key, label, href, live }) => (
          <a
            key={key}
            href={href}
            target={live ? "_blank" : undefined}
            rel={live ? "noopener noreferrer" : undefined}
            data-testid={`atfq-subscribe-${key}`}
            className={`inline-flex items-center gap-2 border px-3 py-2 text-xs tracking-tight transition-colors ${
              live
                ? "border-slate-300 hover:border-[var(--accent)] hover:text-[var(--accent)]"
                : "border-slate-200 text-slate-400"
            }`}
          >
            {label}
            {live ? <ExternalLink className="w-3 h-3" /> : <span className="text-[10px]">soon</span>}
          </a>
        ))}
      </div>
    </div>
  </div>
);

const PillarGrid = () => (
  <div className="mt-20 md:mt-24 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
    {PILLARS.map(({ key, Icon, title, body }) => (
      <div key={key} data-testid={`atfq-pillar-${key}`} className="border-t border-slate-200 pt-6">
        <Icon className="w-5 h-5 text-[var(--accent)] mb-4" />
        <div className="font-display text-2xl leading-tight tracking-tight">{title}</div>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
      </div>
    ))}
  </div>
);

export const ATFQPodcast = () => (
  <section
    id="atfq"
    data-testid="atfq-podcast"
    aria-labelledby="atfq-heading"
    className="relative bg-white text-[#0A192F] border-t border-slate-200"
  >
    <div className="px-6 md:px-12 lg:px-20 py-20 md:py-28 max-w-[1400px] mx-auto">
      <PodcastIntro />

      <PillarGrid />

      <FeaturedVoices />
      <RecordingCalendar />
      <AskAQuestion />
    </div>
  </section>
);

export default ATFQPodcast;
